/**
 * Generic REST layer for every CRM endpoint (the 133 record screens and the extra service operations in crmCatalog.ts).
 * Not run against the live API. It talks to the gateway with fetch and the token from crmConfig.ts.
 *
 * crmClient.ts is still the typed client for the six screens that already exist in CRM.tsx (it maps their field names).
 * This file is raw: records come back exactly as the service sends them (camelCase), and bodies go out exactly as given.
 */
import { CrmApiError } from "./crmClient";

export type Json = null | boolean | number | string | Json[] | { [k: string]: Json };
export interface ApiRecord { id: string; number?: string; version?: string; [k: string]: any }
export interface Paged<T = ApiRecord> { items: T[]; total: number; page: number; pageSize: number }
export interface SchemaField { name: string; type: string; required: boolean; maxLength?: number | null }
export interface Schema { input: SchemaField[]; columns: SchemaField[] }

export interface ApiOptions {
  baseUrl: string;
  getToken: () => string | undefined | Promise<string | undefined>;
  language?: string;
  onUnauthorized?: () => void;
}

export interface Req {
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  form?: FormData;
  headers?: Record<string, string>;
}

export const qs = (q?: Req["query"]): string => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q ?? {})) if (v !== undefined && v !== null && String(v) !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
};

export function createCrmApi(o: ApiOptions) {
  const url = (path: string, q?: Req["query"]) => `${o.baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}${qs(q)}`;

  async function send(method: string, path: string, r: Req = {}): Promise<Response> {
    const headers: Record<string, string> = { ...(r.headers ?? {}) };
    const token = await o.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (o.language) headers["Accept-Language"] = o.language;
    let body: BodyInit | undefined;
    if (r.form) body = r.form; // the browser sets the multipart boundary itself
    else if (r.body !== undefined) { headers["Content-Type"] = "application/json"; body = JSON.stringify(r.body); }
    let res: Response;
    try { res = await fetch(url(path, r.query), { method, headers, body }); }
    catch (e) { throw new CrmApiError(0, "network", "Could not reach the CRM service. Check the connection, and that this site is allowed by the CRM's CORS setting."); }
    if (res.status === 401) o.onUnauthorized?.();
    if (!res.ok) {
      let data: any = null;
      try { data = await res.clone().json(); } catch { /* not JSON */ }
      throw new CrmApiError(res.status, String(data?.code ?? data?.title ?? "error"), String(data?.detail ?? data?.title ?? res.statusText ?? "Request failed"), data?.errors ?? {});
    }
    return res;
  }

  /** JSON in, JSON out. An empty body (204) gives null. */
  async function json<T = any>(method: string, path: string, r?: Req): Promise<T> {
    const res = await send(method, path, r);
    const text = await res.text();
    return (text ? JSON.parse(text) : null) as T;
  }

  const fileName = (res: Response, fallback: string): string => {
    const cd = res.headers.get("Content-Disposition") ?? "";
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    return m ? decodeURIComponent(m[1]) : fallback;
  };

  return {
    json,
    send,
    list: (path: string, p: { page?: number; pageSize?: number; q?: string; sort?: string; filters?: Record<string, string> } = {}) =>
      json<Paged>("GET", path, { query: { page: p.page ?? 1, pageSize: p.pageSize ?? 25, q: p.q, sort: p.sort, ...(p.filters ?? {}) } }),
    schema: (path: string) => json<Schema>("GET", `${path}/schema`),
    get: (path: string, id: string) => json<ApiRecord>("GET", `${path}/${id}`),
    create: (path: string, body: unknown) => json<ApiRecord>("POST", path, { body }),
    /** version is sent as If-Match so two people editing one record do not overwrite each other (409 on a clash). */
    update: (path: string, id: string, body: unknown, version?: string) =>
      json<ApiRecord>("PATCH", `${path}/${id}`, { body, headers: version ? { "If-Match": `"${version}"` } : {} }),
    remove: (path: string, id: string) => json<null>("DELETE", `${path}/${id}`),
    /** Download a result (CSV export or a file endpoint) and hand it to the browser. */
    async download(method: string, path: string, r?: Req, fallbackName = "download"): Promise<void> {
      const res = await send(method, path, r);
      const blob = await res.blob();
      const a = document.createElement("a");
      const href = URL.createObjectURL(blob);
      a.href = href; a.download = fileName(res, fallbackName);
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(href);
    },
  };
}

export type CrmApi = ReturnType<typeof createCrmApi>;

/** Turns a form string into the value the backend expects for a field of this type. Empty means "not sent". */
export function coerce(type: string, raw: string): unknown {
  const s = raw.trim();
  if (s === "") return undefined;
  switch (type) {
    case "integer": return Number.parseInt(s, 10);
    case "number": return Number(s);
    case "boolean": return s === "true";
    case "json": return JSON.parse(s);
    case "datetime": return /Z$|[+-]\d\d:\d\d$/.test(s) ? s : new Date(s).toISOString();
    default: return s; // string, guid, date
  }
}

/** Fills {name} placeholders in a path with values (URL-encoded). Returns the path and the names left unfilled. */
export function fillRoute(path: string, values: Record<string, string>): { path: string; missing: string[] } {
  const missing: string[] = [];
  const out = path.replace(/\{([^}:?]+)[^}]*\}/g, (_m, n: string) => {
    const v = values[n];
    if (!v) { missing.push(n); return ""; }
    return encodeURIComponent(v);
  });
  return { path: out, missing };
}